import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Card,
  Typography,
  Form,
  Input,
  Button,
  Space,
  message,
  Checkbox,
  Spin,
} from 'antd';
import { SendOutlined } from '@ant-design/icons';
import pushPriority from '../../services/pushPriority';
import { parseIds } from '../../utils/parseIds';

const { Title, Paragraph, Text } = Typography;
const { TextArea } = Input;

/**
 * Página para criar push.
 * 
 * Permite criar push para um ou mais CNJs. O push cria automaticamente
 * todas as abrangências disponíveis para cada CNJ.
 */
const PushContent: React.FC = () => {
  const [form] = Form.useForm();
  const [submitting, setSubmitting] = useState(false);
  const navigate = useNavigate();

  // Criar push
  const handleSubmit = async (values: { cnjs: string; makeDownload: boolean }) => {
    const cnjsText = values.cnjs || "";
    if (!cnjsText.trim()) {
      message.error('Digite pelo menos um CNJ');
      return;
    }

    const cnjList = parseIds(cnjsText);
    if (cnjList.length === 0) {
      message.error('Nenhum CNJ válido encontrado');
      return;
    }

    setSubmitting(true);

    try {
      const response = await pushPriority.create({
        cnjs: cnjList,
        makeDownload: values.makeDownload || false,
      });

      if (response.success) {
        const { successCount, errorCount, results } = response.data;
        
        if (successCount > 0) {
          // Salvar IDs gerados no localStorage para monitoramento
          const pushIds = results.map((r) => r.id);
          localStorage.setItem('pushMonitoringIds', JSON.stringify(pushIds));
          localStorage.setItem('pushMonitoringCnjs', JSON.stringify(results.map((r) => ({ id: r.id, cnj: r.cnj }))));
          
          if (errorCount === 0) {
            message.success(`${successCount} push(es) criado(s) com sucesso!`);
          } else {
            message.warning(`${successCount} push(es) criado(s), ${errorCount} erro(s)`);
          }
          
          // Navegar para página de monitoramento
          navigate('/push/monitoring');
        } else {
          message.error(`Falha ao criar push: ${errorCount} erro(s)`);
        }
      } else {
        message.error('Erro ao criar push');
      }
    } catch (error: any) {
      message.error('Erro ao processar requisições: ' + (error.message || 'Erro desconhecido'));
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Card>
      <Space direction="vertical" size="large" style={{ width: '100%' }}>
        <div>
          <Title level={2}>
            <SendOutlined /> Novo Push
          </Title>
          <Paragraph>
            Digite um ou mais CNJs que deseja monitorar. O push criará automaticamente
            todas as abrangências disponíveis para cada CNJ. Você pode digitar um CNJ por linha
            ou um array JSON.
          </Paragraph>
        </div>

        <Form
          form={form}
          name="pushCreate"
          onFinish={handleSubmit}
          layout="vertical"
          initialValues={{
            makeDownload: false,
          }}
        >
          <Form.Item
            name="cnjs"
            label="CNJs"
            rules={[
              { required: true, message: 'Digite pelo menos um CNJ' },
              {
                validator: (_, value) => {
                  if (!value || value.trim().length === 0) {
                    return Promise.reject(new Error('Digite pelo menos um CNJ'));
                  }
                  const cnjList = parseIds(value);
                  if (cnjList.length === 0) {
                    return Promise.reject(new Error('Nenhum CNJ válido encontrado'));
                  }
                  return Promise.resolve();
                },
              },
            ]}
          >
            <TextArea
              rows={10}
              placeholder='Digite os CNJs (um por linha) ou um array JSON: ["0000000-00.0000.0.00.0000", ...]'
              style={{ fontFamily: 'monospace' }}
            />
          </Form.Item>

          <Form.Item name="makeDownload" valuePropName="checked">
            <Checkbox>Make Download</Checkbox>
          </Form.Item>

          <Form.Item>
            <Button type="primary" htmlType="submit" size="large" block loading={submitting}>
              Criar Push
            </Button>
          </Form.Item>
        </Form>
      </Space>
    </Card>
  );
};

/**
 * Componente principal da página Push.
 */
const Push: React.FC = () => {
  return (
    <PushContent />
  );
};

export default Push;
